import { noop } from 'lodash';
import { type ComponentPropsWithoutRef, memo, type ReactNode, useCallback, useMemo, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { AssetGalleryCardSize, useGalleryAssets } from '~/components/Gallery/useGalleryAssets';
import {
  BoardGalleryCardDesktopSize,
  BoardGalleryCardMobileSize,
  useGalleryBoards,
} from '~/components/Gallery/useGalleryBoards';
import { FileGalleryCardSize, useGalleryFiles } from '~/components/Gallery/useGalleryFiles';
import { type UseGallerySectionHeaderParams } from '~/components/Gallery/useGallerySectionHeader';
import LoadMoreSpinner from '~/components/LoadMoreSpinner';
import { PublicBoardGalleryAsset } from '~/components/PublicBoard/PublicBoardGalleryAsset';
import { PublicBoardGalleryBoard } from '~/components/PublicBoard/PublicBoardGalleryBoard';
import { PublicBoardGalleryFile } from '~/components/PublicBoard/PublicBoardGalleryFile';
import { PublicBoardNullState } from '~/components/PublicBoard/PublicBoardNullState';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import {
  SelectableGalleryView,
  type SelectableGalleryViewProps,
} from '~/components/SelectableGalleryView/SelectableGalleryView';
import { ASSET_CARD_ROW_MT } from '~/constants/assetCard';
import { WKSPC_CLIP_INFO_HEIGHT, WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from '~/constants/WorkspaceSpacing';
import { useCardSizePreference } from '~/hooks/useCardSizePreference';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { isTitleAndMetadataVisibleSelector, visibleCustomFieldsLengthSelector } from '~/store/configViews/selectors';
import { usePublicGalleryViewItems } from '~/swr-hooks/gallery/galleryView/usePublicGalleryViewItems';
import { canChangeAssetCustomFields } from '~/utils/permissions/assetPermissions';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const FILE_GALLERY_MOBILE_ITEM_WIDTH = 300;

export type PublicBoardGalleryViewProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'> &
  Pick<SelectableGalleryViewProps, 'scrollElementRef'> &
  Pick<UseGallerySectionHeaderParams, 'renderHeader'> & {
    assetItemHorizontalSpacing?: number;
    boardItemHorizontalSpacing?: number;
    containerHorizontalPadding?: number;
    emptyStateHeader?: ReactNode;
    fileItemHorizontalSpacing?: number;
    headerContainerHeight?: number;
  };

export const PublicBoardGalleryView = memo(
  ({
    className,
    scrollElementRef,
    renderHeader,
    assetItemHorizontalSpacing,
    boardItemHorizontalSpacing,
    containerHorizontalPadding,
    emptyStateHeader,
    fileItemHorizontalSpacing,
    headerContainerHeight,
  }: PublicBoardGalleryViewProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const {
      data: { boards: boardsData, clips: clipsData, files: filesData },
      isLoading,
      loadNextPage,
      isEmpty,
    } = usePublicGalleryViewItems();

    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const visibleCustomFieldsLength = useAirSelector(visibleCustomFieldsLengthSelector);

    const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();
    const { cardSize } = useCardSizePreference();

    useGroupedGallerySelectedItems(boardsData.items, clipsData.items, filesData.items);

    const { width = 0, ref: widthRef } = useResizeDetector<HTMLDivElement>({
      handleHeight: false,
      refreshMode: 'throttle',
      refreshRate: 300,
    });

    const { permissions } = usePublicPermissions();

    const canDownload = canGenerateZip(permissions);

    const isClipSelectable = useCallback(
      () => canChangeAssetCustomFields(permissions) || canDownload,
      [canDownload, permissions],
    );

    const isBoardSelectable = useCallback(
      () => canChangeBoardCustomFields(permissions) || canDownload,
      [canDownload, permissions],
    );

    const boardsSection = useGalleryBoards({
      data: boardsData,
      containerWidth: width,
      renderBoard: ({ data, index, box }) => <PublicBoardGalleryBoard data={data} index={index} box={box} />,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      itemHorizontalSpacing: boardItemHorizontalSpacing,
      headerContainerHeight,
      isSelectable: isBoardSelectable,
      itemHeight: isAboveSmallScreen ? BoardGalleryCardDesktopSize[cardSize] : BoardGalleryCardMobileSize[cardSize],
      renderHeader,
    });

    const fileAdditionalHeight = useMemo(() => {
      const additionalRows = Math.max(0, visibleCustomFieldsLength - 2);

      return additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [visibleCustomFieldsLength]);

    const assetAdditionalHeight = useMemo(() => {
      let height = 0;

      if (isTitleAndMetadataVisible) {
        height += WKSPC_CLIP_INFO_HEIGHT;
      }
      if (visibleCustomFieldsLength) {
        height += ASSET_CARD_ROW_MT;
      }

      return height + visibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [visibleCustomFieldsLength, isTitleAndMetadataVisible]);

    const assetsSection = useGalleryAssets({
      renderAsset: ({ data, index, box }) => <PublicBoardGalleryAsset data={data} index={index} box={box} />,
      isFirstSection: !boardsData.items.length,
      data: clipsData,
      containerWidth: width,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      itemAdditionalHeight: assetAdditionalHeight,
      itemHorizontalSpacing: assetItemHorizontalSpacing,
      isSelectable: isClipSelectable,
      itemHeight: AssetGalleryCardSize[cardSize],
      renderHeader,
      headerContainerHeight,
    });

    const filesSection = useGalleryFiles({
      renderFile: ({ data, index, box }) => <PublicBoardGalleryFile data={data} index={index} box={box} />,
      isFirstSection: !boardsData.items.length && !clipsData.items.length,
      data: filesData,
      containerWidth: width,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      isSelectable: isClipSelectable,
      itemHeight: FileGalleryCardSize[cardSize] + fileAdditionalHeight,
      itemWidth: !isAboveMediumScreen ? FILE_GALLERY_MOBILE_ITEM_WIDTH : undefined,
      itemHorizontalSpacing: fileItemHorizontalSpacing ?? (!isAboveSmallScreen ? 0 : 24),
      renderHeader,
      headerContainerHeight,
    });

    const galleryBody = useMemo(() => {
      if (isLoading || typeof document === 'undefined') {
        return <LoadMoreSpinner isVisible isLoading={isLoading} loadMore={noop} />;
      }

      if (isEmpty) {
        return (
          <>
            {emptyStateHeader}
            <PublicBoardNullState />
          </>
        );
      }

      if (!width) {
        return null;
      }

      const sections = [boardsSection, assetsSection, filesSection].filter(
        (section): section is GallerySection<GalleryMetadata> => !!section,
      );

      return (
        <div className={cn('relative mt-6 min-h-full', className)} ref={containerRef}>
          <SelectableGalleryView
            scrollElementRef={scrollElementRef}
            containerRef={containerRef}
            sections={sections}
            width={width}
          />
        </div>
      );
    }, [
      assetsSection,
      boardsSection,
      className,
      emptyStateHeader,
      filesSection,
      isEmpty,
      isLoading,
      scrollElementRef,
      width,
    ]);

    return (
      <div className="mx-auto w-full">
        <div ref={widthRef} />
        {galleryBody}
      </div>
    );
  },
);

PublicBoardGalleryView.displayName = 'PublicBoardGalleryView';
